import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { zerlegeRechtsText } from '../../lib/rechtsText';
import { TimeNYzerWortmarke } from '../anmeldung/TimeNYzerWortmarke';
import { TIMENYZER_VERLAUF } from '../anmeldung/AnmeldeRahmen';
import { Fusszeile } from '../anmeldung/Fusszeile';

const LINK = 'text-[#1d4ed8] underline-offset-2 hover:underline';

// Kleine Bausteine ohne verschachtelte .map über Objekt-Eigenschaften (das Visual-Edits-Babel-Plugin läuft sonst in eine Endlosschleife).
const InlineTeil = ({ teil }) => {
  const { typ, text, ziel, extern } = teil;
  if (typ === 'fett') return <strong className="font-semibold text-[#0e1628]">{text}</strong>;
  if (typ === 'link' && extern) return <a href={ziel} target="_blank" rel="noopener noreferrer" className={LINK}>{text}</a>;
  if (typ === 'link') return <Link to={ziel} className={LINK}>{text}</Link>;
  return text;
};

const Inline = ({ teile }) => teile.map((teil, i) => <InlineTeil key={i} teil={teil} />);

const Liste = ({ punkte }) => (
  <ul className="mt-4 list-disc space-y-1.5 pl-6 text-base leading-[1.6] text-[#334155]">
    {punkte.map((punkt, i) => <li key={i}><Inline teile={punkt} /></li>)}
  </ul>
);

const Absatz = ({ zeilen }) => (
  <p className="mt-4 break-words text-base leading-[1.6] text-[#334155]">
    {zeilen.map((zeile, i) => <React.Fragment key={i}>{i > 0 && <br />}<Inline teile={zeile} /></React.Fragment>)}
  </p>
);

const Block = ({ block }) => {
  const { typ, text, punkte, zeilen } = block;
  if (typ === 'titel') return <h1 className="text-[30px] font-bold leading-[1.25] tracking-[-0.022em] text-[#0e1628]" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="recht-titel">{text}</h1>;
  if (typ === 'abschnitt') return <h2 className="mt-10 border-b border-[#e2e8f0] pb-2 text-xl font-semibold text-[#0e1628]" style={{ fontFamily: "'Inter', sans-serif" }}>{text}</h2>;
  if (typ === 'fett') return <p className="mt-4 font-semibold text-[#0e1628]">{text}</p>;
  if (typ === 'liste') return <Liste punkte={punkte} />;
  return <Absatz zeilen={zeilen} />;
};

// Impressum/Datenschutz: öffentlich, angemeldet und abgemeldet (Vorbild PersoNYzer RechtsSeite.js).
export const RechtsSeite = ({ text, testid }) => {
  const navigate = useNavigate();
  const bloecke = useMemo(() => zerlegeRechtsText(text), [text]);
  const zurueck = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/login'));
  return (
    <div className="flex min-h-screen flex-col bg-[#f8fafc]" data-testid={testid}>
      <header className="flex h-16 items-center justify-between px-5 sm:px-6" style={{ background: TIMENYZER_VERLAUF }}>
        <button type="button" onClick={zurueck} className="inline-flex items-center gap-2 rounded-md px-2 py-1 text-sm font-medium text-white transition-colors hover:bg-white/10" data-testid="recht-zurueck">
          <ArrowLeft className="h-4 w-4" />Zurück
        </button>
        <TimeNYzerWortmarke groesse="klein" />
      </header>
      <main className="mx-auto w-full max-w-[720px] flex-1 px-5 pt-10 sm:px-6" data-testid="recht-inhalt">
        {bloecke.map((block, i) => <Block key={i} block={block} />)}
      </main>
      <Fusszeile />
    </div>
  );
};

export default RechtsSeite;
